/**
 * The long form of the Digital Shelf KPI descriptions, for the Info Center glossary.
 *
 * The short form of the same descriptions is the column tooltip (`@constants/kpiHelp.js`).
 * Keep the two in step: the tooltip is the summary, this is the full explanation.
 *
 * Content only — no JSX here, so the texts can be reviewed without reading the layout.
 */

/** The four scores the Digital Shelf Score averages, for the composition diagram. */
export const SCORE_COMPOSITION = ['Stock Score', 'Assortment Score', 'Product Page Score', 'Share of Search Score']

/**
 * The colour bands of the score circles, transcribed from the `pattern` the Digital Shelf
 * table passes to `CircularPercentageSeeker`. The component picks the first band whose
 * threshold the value reaches (`value >= threshold`), so the ranges below are inclusive.
 */
export const SCORE_COLOR_SCALE = [
	{ range: '89 – 100', color: 'success.light' },
	{ range: '79 – 88', color: 'success.lighter' },
	{ range: '69 – 78', color: 'warning.lighter' },
	{ range: '49 – 68', color: 'warning.light' },
	{ range: '29 – 48', color: 'error.lighter' },
	{ range: '0 – 28', color: 'error.light' }
]

/** What the arrow under each score means. */
export const TREND_LEGEND = [
	{ icon: 'arrow-up', color: 'success.main', text: 'Higher than the previous period' },
	{ icon: 'arrow-down', color: 'error.main', text: 'Lower than the previous period' },
	{ icon: 'minus', color: 'disabled.dark', text: 'No change' }
]

export const GLOSSARY_INTRO = {
	title: 'How to read the Digital Shelf table',
	blocks: [
		{
			label: 'What the figures are',
			text: 'Seven scores from 0 to 100, for the period selected at the top right of the dashboard.'
		},
		{
			label: 'What a row is',
			text: 'One combination of the dimensions chosen in Split By: market, retailer, brand, or any mix of ' +
				'them. Every score on a row is calculated over that row\'s scope alone — split by brand and the row ' +
				'is that brand across all its retailers; add retailer and it becomes one row per brand and retailer.'
		},
		{
			label: 'Filters versus Split By',
			text: 'The filters above the table (Market, Retailers, Brands, SKU Group, SKUs) decide what enters the ' +
				'calculation. Split By decides how it is broken into rows.'
		},
		{
			label: 'The Only Changes toggle',
			text: 'Hides the rows where nothing moved between the two periods.'
		}
	]
}

export const GLOSSARY_SECTIONS = [
	{
		id: 'digital-shelf-score',
		title: 'Digital Shelf Score',
		icon: 'gauge-high',
		accent: 'primary',
		chip: 'Overall',
		lead: 'The overall health of your presence within this row\'s scope, in one number: are the products ' +
			'listed, available, well presented and visible in search.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'The plain average of the four scores to its right — Stock, Assortment, Product Page and ' +
					'Share of Search. All four weigh the same. Scores that cannot be measured are left out of the ' +
					'average, not counted as zero: a retailer with no Share of Search tracking is scored on the ' +
					'other three and is not penalised for the missing one.'
			},
			{
				label: 'How to read it',
				text: 'A low score does not say what is wrong — the four columns to its right do. It is the ' +
					'headline number, not the diagnosis.'
			}
		],
		table: {
			head: ['Stock', 'Assortment', 'Product Page', 'Share of Search', 'Digital Shelf Score'],
			rows: [
				['84', '95', '97', '10', '72 — average of four'],
				['84', '95', '97', 'not tracked', '92 — average of three']
			]
		}
	},
	{
		id: 'stock-score',
		title: 'Stock Score',
		icon: 'boxes-stacked',
		accent: 'info',
		chip: 'Availability',
		lead: 'How consistently your products were available to buy during the period, within this row\'s scope.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'Product pages that reported the product as available, over all product pages observed in ' +
					'the period. Every product counts once per day, so a product that is out of stock for half the ' +
					'period contributes to both halves of the fraction. On the retailers configured for it, ' +
					'availability for collection in store counts as available.'
			},
			{
				label: 'How to read it',
				text: '100 % means every product was buyable every day. A drop can mean a genuine stock-out, or a ' +
					'product that went off-sale.'
			}
		]
	},
	{
		id: 'assortment-score',
		title: 'Assortment Score',
		icon: 'list-check',
		accent: 'info',
		chip: 'Coverage',
		lead: 'Listing coverage: how much of the assortment expected within this row\'s scope was actually found.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'Products found, over the products expected to be listed during the period. The score is ' +
					'capped at 100, so finding more than expected does not push it above full marks. Filters change ' +
					'both halves: selecting specific markets, retailers, brands or SKUs narrows not only what is ' +
					'counted, but also what is expected.'
			},
			{
				label: 'How to read it',
				text: 'This is the one column that is not about content quality. A retailer can score 100 here ' +
					'and score poorly everywhere else — it simply means everything expected is listed.'
			}
		]
	},
	{
		id: 'product-page-score',
		title: 'Product Page Score',
		icon: 'rectangle-list',
		accent: 'secondary',
		chip: 'Content',
		lead: 'The content quality of your product pages in this row: images, title, description, bullet points, ' +
			'enhanced content and the rest of the page KPIs.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'It is exactly the Weighted Score of the Product Pages KPIs screen — the same number, not ' +
					'an approximation. Each page KPI is scored as a percentage and then combined using the weight ' +
					'given to it on that retailer, so retailers can be judged against different content priorities.'
			},
			{
				label: 'How to read it',
				text: 'Use this column to spot which retailer needs attention, then open the Product Pages KPIs ' +
					'screen to see which KPI is pulling the score down.'
			}
		]
	},
	{
		id: 'share-of-search-score',
		title: 'Share of Search Score',
		icon: 'magnifying-glass',
		accent: 'warning',
		chip: 'Visibility',
		lead: 'How much of the tracked search results are yours, within this row\'s scope.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'Your products appearing in the tracked search results, over every result within the first ' +
					'100 positions — yours and the competition\'s. Results below position 100 are not counted. ' +
					'Computed per keyword and day, then averaged.'
			},
			{
				label: 'How to read it',
				text: 'This is a competitive share, not a compliance score: unlike the other columns, 100 % is ' +
					'not the realistic target. What matters is the direction of the arrow and the comparison ' +
					'between retailers and markets.'
			},
			{
				label: 'Example',
				text: 'For a tracked keyword, the search returns 150 results. Only the first 100 count, and 8 of ' +
					'them are yours → 8 % for that keyword and day.'
			}
		]
	},
	{
		id: 'share-of-search-organic-score',
		title: 'Share of Search Organic Score',
		icon: 'seedling',
		accent: 'success',
		chip: 'Visibility · unpaid',
		lead: 'The part of your Share of Search won by unpaid search results.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'Your products appearing in unpaid results, over the same total as the overall Share of ' +
					'Search — every result within the first 100 positions.'
			},
			{
				label: 'How to read it',
				text: 'Because both this and the sponsored column use the same denominator, they add up to the ' +
					'overall Share of Search. That makes it directly readable: how much of your visibility you are ' +
					'earning rather than buying.'
			},
			{
				label: 'Example',
				text: 'Share of Search 10 %, of which Organic 8 % and Sponsored 2 % → four fifths of your ' +
					'visibility in that row is unpaid.'
			}
		]
	},
	{
		id: 'share-of-search-sponsored-score',
		title: 'Share of Search Sponsored Score',
		icon: 'bullhorn',
		accent: 'warning',
		chip: 'Visibility · paid',
		lead: 'The part of your Share of Search won by paid results.',
		blocks: [
			{
				label: 'How it is calculated',
				text: 'Your products appearing in sponsored results, over the same total as the overall Share of ' +
					'Search — every result within the first 100 positions.'
			},
			{
				label: 'How to read it',
				text: 'Read it together with the organic column: a sponsored share that grows while the organic ' +
					'one stays flat means visibility is being bought rather than gained.'
			}
		]
	}
]
